// src/components/email/EmailGrupoCreate.jsx
// ═══════════════════════════════════════════════════════════════════════════
// 📧 CREAR GRUPO DE EMAIL MARKETING
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';

const EmailGrupoCreate = () => {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState('manual');
  const [loading, setLoading] = useState(false);

  const handleGuardar = async () => {
    if (!nombre) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo Requerido',
        text: 'El nombre del grupo es obligatorio'
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/email/grupos', {
        nombre,
        descripcion,
        tipo
      });

      Swal.fire({
        icon: 'success',
        title: '✅ Grupo Creado',
        text: 'El grupo ha sido creado exitosamente',
        timer: 2000,
        showConfirmButton: false
      });

      navigate('/email/grupos');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo crear el grupo'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>➕ Nuevo Grupo de Email</h1>
          <p style={styles.subtitle}>Crea un grupo para segmentar tus contactos</p>
        </div>
        <button onClick={() => navigate('/email/grupos')} style={styles.btnSecondary}>
          ← Volver
        </button>
      </div>

      {/* Formulario */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Información del Grupo</h2>

        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre del Grupo *</label>
          <input
            type="text"
            style={styles.input}
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            placeholder="Ej: Clientes Sector Construcción"
            disabled={loading}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Descripción</label>
          <textarea
            style={styles.textarea}
            value={descripcion}
            onChange={e => setDescripcion(e.target.value)}
            placeholder="Descripción del propósito del grupo..."
            disabled={loading}
            rows="4"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Tipo de Grupo</label>
          <select
            style={styles.select}
            value={tipo}
            onChange={e => setTipo(e.target.value)}
            disabled={loading}
          >
            <option value="manual">Manual - Asignar miembros uno a uno</option>
            <option value="csv">CSV - Importar desde archivo</option>
            <option value="automatico">Automático - Por criterios (próximamente)</option>
          </select>
        </div>

        <div style={styles.infoBox}>
          <h4 style={styles.infoTitle}>💡 Tipos de Grupo</h4>
          <ul style={styles.infoList}>
            <li><strong>Manual:</strong> Agrega clientes individualmente desde tu base de datos</li>
            <li><strong>CSV:</strong> Importa contactos desde un archivo Excel/CSV</li>
            <li><strong>Automático:</strong> Segmenta por criterios (ej: todos los clientes activos)</li>
          </ul>
        </div>

        <div style={styles.actions}>
          <button
            onClick={handleGuardar}
            disabled={loading || !nombre}
            style={{
              ...styles.btnPrimary,
              opacity: (loading || !nombre) ? 0.6 : 1,
              cursor: (loading || !nombre) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '⏳ Guardando...' : '💾 Guardar Grupo'}
          </button>
          <button
            onClick={() => navigate('/email/grupos')}
            style={styles.btnSecondary}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: 'calc(100vh - 70px)',
    background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
    padding: '24px 32px',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  btnSecondary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    maxWidth: '800px'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e5e7eb'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    background: 'white',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    background: 'white',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    background: 'white',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  infoBox: {
    padding: '16px',
    background: '#eff6ff',
    borderRadius: '8px',
    borderLeft: '4px solid #3b82f6',
    marginBottom: '20px'
  },
  infoTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: '12px'
  },
  infoList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '12px',
    color: '#1e40af',
    lineHeight: '2'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    paddingTop: '20px'
  },
  btnPrimary: {
    flex: 1,
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};

export default EmailGrupoCreate;